import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { Flex, Text } from '@radix-ui/themes';
import { Accordion, Callout, PillToggle } from '@/components/common';
import { SPOT_GROUP_DEFINITIONS } from '@/constants';
import { useVenues } from '@/hooks';
import { monoCounterStyle } from './styles';

export const MAX_SPOTS = 6;

interface SpotsPickerProps {
  value: string[];
  onChange: (value: string[]) => void;
}

/** Shared spots accordion + pills — onboarding step and edit-profile spots page. */
export function SpotsPicker({ value, onChange }: SpotsPickerProps) {
  const { t } = useTranslation('onboarding');
  const { venues, isLoading } = useVenues();

  const groupedSpots = useMemo(
    () =>
      SPOT_GROUP_DEFINITIONS.map(group => ({
        ...group,
        items: venues
          .filter(venue => group.categories.includes(venue.category))
          .map(venue => venue.name)
          .sort(),
      })).filter(group => group.items.length > 0),
    [venues]
  );

  return (
    <Flex direction="column" gap="4">
      <Callout>{t('steps.spots.callout')}</Callout>
      <Text size="1" color="gray" style={monoCounterStyle}>
        {t('steps.spots.counter', { count: value.length, max: MAX_SPOTS })}
      </Text>
      {isLoading ? (
        <Text size="2" color="gray">
          {t('steps.spots.loading')}
        </Text>
      ) : (
        <Accordion type="multiple">
          {groupedSpots.map(group => (
            <Accordion.Item
              key={group.id}
              value={group.id}
              title={group.label}
              badge={group.items.filter(i => value.includes(i)).length}
            >
              <PillToggle
                className="contents"
                type="multiple"
                options={group.items}
                value={value}
                onValueChange={onChange}
                disabledOptions={group.items.filter(
                  opt => !value.includes(opt) && value.length >= MAX_SPOTS
                )}
              />
            </Accordion.Item>
          ))}
        </Accordion>
      )}
    </Flex>
  );
}
